import {
  ErroDeAutenticacao,
  type AdaptadorDeAuth,
  type ResultadoDeEntrada,
} from '@/auth/adaptadores/tipos'
import {
  CODIGO_EXPIRADO_MOCK,
  CODIGO_REDEFINICAO_MOCK,
  comoSessao,
  fixturePorEmail,
  SENHA_MOCK,
  SENHA_TEMPORARIA_MOCK,
} from '@/auth/fixtures/sessoes'
import { POLITICA_PADRAO, senhaAtendePolitica, type PoliticaDeSenha } from '@/auth/politicaDeSenha'
import type { Sessao } from '@/auth/session'

/**
 * Mocked session — story 0.3 AC-7.
 *
 * The Cognito pool does not exist until the `CONEXÃO` schedule line (14/ago+), and
 * screens are being built now. This adapter fills the same port as the real one, so
 * every screen written against `useSession()` keeps working when the pool lands and
 * the swap touches nothing but `escolherAdaptador`.
 *
 * It deliberately reproduces the paths that are easy to get wrong rather than always
 * succeeding — wrong password, unknown e-mail, and the `FORCE_CHANGE_PASSWORD`
 * challenge of a first login — so the pt-BR error mapping (AC-9) and the "defina sua
 * senha" screen are both exercised long before the pool exists.
 */

/** Exported so tests can seed a session without driving the login form. */
export const CHAVE_SESSAO_MOCK = 'iox.sessao-mock'
const CHAVE = CHAVE_SESSAO_MOCK

/**
 * E-mails that already completed first login.
 *
 * In memory and **cleared on logout**, so the `Convidado` fixture replays the first
 * access every time — the whole reason it exists is to be demonstrable on demand.
 * A real account naturally does this once.
 */
const senhaJaDefinida = new Set<string>()

/**
 * `sessionStorage`, not `localStorage`: a mocked identity must not outlive the tab.
 * Closing the browser is the cheapest way back to a clean state while demoing the
 * personas in a row — including replaying the first-login flow.
 */
function armazenamento(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    // Storage can throw outright under a strict cookie policy. A session that
    // survives only in memory is better than a login screen that will not mount.
    return null
  }
}

/** Small, fixed latency so loading states are visible while developing. */
const ATRASO_MS = 120

function esperar(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ATRASO_MS))
}

/**
 * The challenge opened by `entrar()`, awaiting `definirNovaSenha()`.
 *
 * In memory and NOT persisted, matching Amplify: a reload drops the challenge and
 * the user signs in again. Persisting it would make the mock behave better than the
 * real thing, which is the one way a mock can actively mislead.
 */
let desafioEmAberto: string | null = null

/**
 * E-mails with a reset code outstanding, so `confirmarRedefinicaoDeSenha` can refuse a
 * code that was never requested. In memory, like the challenge above — the real pool
 * does not care whether the browser reloaded, but nothing here should behave BETTER
 * than the pool.
 */
const codigosEmAberto = new Set<string>()

/**
 * Test seam. `desafioEmAberto`, `senhaJaDefinida` and `codigosEmAberto` are all module
 * state that outlives a React unmount — without this, one test completing first access
 * leaves the `Convidado` fixture "already activated" for every test after it, and one
 * test requesting a reset code leaves that code valid for the next.
 */
export function reiniciarMockParaTeste(): void {
  desafioEmAberto = null
  senhaJaDefinida.clear()
  codigosEmAberto.clear()
}

export const adaptadorMock: AdaptadorDeAuth = {
  modo: 'mock',

  async restaurar(): Promise<Sessao | null> {
    const email = armazenamento()?.getItem(CHAVE)
    if (!email) return null
    const fixture = fixturePorEmail(email)
    // A fixture renamed between reloads leaves a dangling e-mail. Drop it rather
    // than resurrecting a session for a persona that no longer exists.
    if (!fixture) {
      armazenamento()?.removeItem(CHAVE)
      return null
    }
    return comoSessao(fixture)
  },

  async entrar(email: string, senha: string): Promise<ResultadoDeEntrada> {
    await esperar()
    desafioEmAberto = null

    const fixture = fixturePorEmail(email)
    if (!fixture) throw new ErroDeAutenticacao('UserNotFoundException')

    // A convite-only account starts on a temporary password and must choose a real
    // one before it ever holds a session.
    if (fixture.primeiroAcesso && !senhaJaDefinida.has(fixture.email)) {
      if (senha !== SENHA_TEMPORARIA_MOCK) throw new ErroDeAutenticacao('NotAuthorizedException')
      desafioEmAberto = fixture.email
      return { tipo: 'novaSenhaRequerida' }
    }

    if (senha !== SENHA_MOCK) throw new ErroDeAutenticacao('NotAuthorizedException')

    armazenamento()?.setItem(CHAVE, fixture.email)
    return { tipo: 'sessao', sessao: comoSessao(fixture) }
  },

  async definirNovaSenha(novaSenha: string): Promise<Sessao> {
    await esperar()

    // Amplify throws when there is no challenge in flight; so does this.
    if (!desafioEmAberto) throw new ErroDeAutenticacao('SessaoDeDesafioExpirada')

    // Stands in for the pool's own rejection, so the `InvalidPasswordException`
    // branch of the screen is reachable in mock mode too.
    if (!senhaAtendePolitica(novaSenha, POLITICA_PADRAO)) {
      throw new ErroDeAutenticacao('InvalidPasswordException')
    }

    const fixture = fixturePorEmail(desafioEmAberto)
    if (!fixture) throw new ErroDeAutenticacao('UserNotFoundException')

    // From here on the persona signs in like any other, with `SENHA_MOCK`. The
    // chosen password is NOT stored: mock mode has no password store, and inventing
    // one would only produce a credential to leak.
    senhaJaDefinida.add(fixture.email)
    desafioEmAberto = null
    armazenamento()?.setItem(CHAVE, fixture.email)
    return comoSessao(fixture)
  },

  async solicitarCodigoDeRedefinicao(email: string): Promise<void> {
    await esperar()

    // Resolves whether or not the account exists — see the port's note. The mock is
    // where an enumeration leak would be easiest to introduce by accident, because
    // "throw when the fixture is missing" is the obvious shape and it is wrong here.
    const fixture = fixturePorEmail(email)
    if (fixture) codigosEmAberto.add(fixture.email)
  },

  async confirmarRedefinicaoDeSenha(
    email: string,
    codigo: string,
    novaSenha: string,
  ): Promise<void> {
    await esperar()

    if (codigo === CODIGO_EXPIRADO_MOCK) throw new ErroDeAutenticacao('ExpiredCodeException')

    const fixture = fixturePorEmail(email)
    // An unknown e-mail never received a code, so "wrong code" is both true and
    // non-revealing. Answering `UserNotFoundException` here would undo the care taken
    // one method up.
    if (!fixture || !codigosEmAberto.has(fixture.email) || codigo !== CODIGO_REDEFINICAO_MOCK) {
      throw new ErroDeAutenticacao('CodeMismatchException')
    }

    if (!senhaAtendePolitica(novaSenha, POLITICA_PADRAO)) {
      throw new ErroDeAutenticacao('InvalidPasswordException')
    }

    // The code is single-use, as it is on the pool.
    codigosEmAberto.delete(fixture.email)

    // The chosen password is NOT stored — mock mode has no password store, and
    // inventing one only produces a credential to leak. From here the persona signs
    // in with `SENHA_MOCK` like the others; marking it activated keeps a `Convidado`
    // who just reset from being bounced back into first access.
    senhaJaDefinida.add(fixture.email)
  },

  async politicaDeSenha(): Promise<PoliticaDeSenha> {
    return POLITICA_PADRAO
  },

  async sair(): Promise<void> {
    desafioEmAberto = null
    codigosEmAberto.clear()
    // Lets the `Convidado` fixture replay the first access on the next login.
    senhaJaDefinida.clear()
    armazenamento()?.removeItem(CHAVE)
  },

  async obterToken(): Promise<string | null> {
    const email = armazenamento()?.getItem(CHAVE)
    if (!email) return null
    // Not a JWT and not shaped like one on purpose. Anything that tries to DECODE
    // this instead of forwarding it as an opaque credential should break loudly —
    // permissions come from the API, never from token claims (§5.1 / AC-6).
    return `mock-token:${email}`
  },
}
